// THE SETUP HUB'S RULES, AS PURE FUNCTIONS — what may be done in a given state,
// and how the operator's three quorum fields become the worker's body.
//
// ── WHY THIS IS A MODULE AND NOT INLINE IN THE COMPONENTS ──────────────────
// This package has NO DOM test environment (README: "no DOM test environment"),
// so a rule that lives inside a React callback is held by reading the code and
// nothing else. The rules that decide whether a point-of-no-return act is OFFERED,
// and whether the number about to be sent is well formed, are the ones most worth
// a check that can fail — so they live here, pure over their arguments, and
// `state.test.ts` drills each in both directions. The components are then a thin
// form over these answers.
//
// ── IT IMPORTS ONLY A TYPE ─────────────────────────────────────────────────
// `QuorumDeclaration` is `import type`, fully erased at runtime, so this module has
// no runtime dependency on `../api/client` (which pulls @cca/ui) and the test is
// hermetic in the default node environment — the same property `../paper` has.

import type { QuorumDeclaration } from '../api/client';

// ═══════════════════════════════════════════════════════════════════════════
//  THE ROLL — which acts the panel offers, by phase.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The two things the roll can be from the surface's point of view.
 *
 * **There is deliberately no 'empty' vs 'loaded' distinction here, because the
 * worker exposes no way to read one.** The setup surface has a GET for drafts and
 * one for the frozen register, but NONE for a pre-freeze elector count — so a
 * fresh page load cannot tell an empty roll from a loaded-but-unfrozen one. Frozen
 * is detected by asking the attendance register, which answers only once the roll
 * is frozen (`roll_not_frozen` otherwise). A positive elector count exists only
 * after a load made THIS session, which is carried separately.
 */
export type RollPhase = 'frozen' | 'not-frozen';

/** Which roll acts the panel offers. */
export interface RollActs {
  /** Pull/replace the roster — pre-freeze only, when the roll is not yet fixed. */
  pull: boolean;
  /** Freeze — the one-way act, pre-freeze only AND only with a roll actually
   *  loaded (Miranda: "appears only when a valid roll is loaded"). */
  freeze: boolean;
}

/**
 * Offer each roll act only when the state permits it.
 *
 * A FROZEN roll offers neither — it is fixed, and a change to it is the amendment
 * path, out of this surface. A NOT-FROZEN roll offers a (re)load always, and the
 * freeze only when a roll has actually been loaded this session: freezing an empty
 * roll earns `clergy_house_empty` from the engine, so the button is not offered
 * against a roll the surface cannot confirm exists.
 */
export function permittedRollActs(phase: RollPhase, hasLoadedRoll: boolean): RollActs {
  if (phase === 'frozen') return { pull: false, freeze: false };
  return { pull: true, freeze: hasLoadedRoll };
}

// ═══════════════════════════════════════════════════════════════════════════
//  THE BALLOTS — which lifecycle acts a draft offers.
// ═══════════════════════════════════════════════════════════════════════════

/** Which lifecycle acts one ballot offers, in a given roll phase. */
export interface BallotActs {
  markReady: boolean;
  abandon: boolean;
  open: boolean;
  /** GIVE A RUNNING POLL MORE TIME — `POST …/ballot/:id/extend`. The fourth act, and
   *  the only one this surface offers on a ballot that has already opened. */
  extend: boolean;
}

/**
 * Offer each lifecycle act only when the state permits it — the hub GUIDES the
 * lawful order rather than claiming control of it.
 *
 *  · `markReady` from 'draft' ALONE — a ready ballot has already been marked.
 *  · `abandon`   from 'draft' OR 'ready' — the one correction a ballot that has
 *    left draft has; a ballot that has polled is closed, never abandoned.
 *  · `open`      from 'ready' AND only once the roll is FROZEN. The worker does
 *    NOT gate ready on a frozen roll (a ballot is built before the freeze, SO
 *    15.5), so this is the UI guiding the two tracks to converge: opening needs
 *    the attendance register to declare a quorum against, and the register does
 *    not exist until the roll is frozen. Offering Open on an unfrozen sitting
 *    would lead the operator to a wall, so it is withheld until the freeze.
 *  · `extend`    from 'open' ALONE — the poll must be RUNNING for there to be
 *    anything to give more time to, which is the worker's own gate 2 (exactly
 *    'open', `src/routes/setup.ts`) rather than a guess about it. It does NOT
 *    consult `rollFrozen`: a ballot cannot reach 'open' on an unfrozen roll, so
 *    testing it again would be a second, weaker copy of a fence already passed.
 *
 * **THE STATE WORD IS THE WHOLE ANSWER HERE, AND THE WINDOW IS NOT.** Whether a
 * running poll has a close to move, and whether that close has already passed, are
 * facts about the ballot's SCHEDULE, not its state — this function is not handed one
 * and must not grow an argument for it. `OpenPollsPanel` decides those two from the
 * row's own `schedule`; this decides only whether the act belongs to the ballot at
 * all. Keeping the split means a state-word mistake cannot hide inside a window test.
 *
 * The server re-checks every one of these; a withheld act is courtesy, and a
 * `wrong_state` refusal is the server correcting the hub's belief.
 */
export function permittedBallotActs(state: string, rollFrozen: boolean): BallotActs {
  const draft = state === 'draft';
  const ready = state === 'ready';
  return {
    markReady: draft,
    abandon: draft || ready,
    open: ready && rollFrozen,
    extend: state === 'open',
  };
}

// ═══════════════════════════════════════════════════════════════════════════
//  THE OPEN STEP — the quorum declaration, from three fields to one body.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The three per-House quorum fields as the operator types them — STRINGS, because
 * an empty field is not a zero and the two must stay distinguishable: a blank
 * Clergy box is "not answered yet", while a typed `0` is "nobody from Clergy is
 * present", which is a lawful, honest declaration of an inquorate House.
 */
export interface QuorumInputs {
  clergy: string;
  laity: string;
  bishop: string;
}

/**
 * The open form's initial values, and it is the WHOLE reason "reference, not
 * prefilled" is a fact a test can pin.
 *
 * **THE FIELDS START EMPTY, NEVER SEEDED FROM THE ATTENDANCE REGISTER** (registrar
 * ruling 2026-08-16, restated by Steve 2026-08-29): quorum is a DECLARED act, not
 * a derived one. The register's per-House counts are shown BESIDE these fields as
 * read-only reference so the operator can see what the room holds — but a
 * pre-filled field is a computed quorum wearing a declaration's clothes, and this
 * function refuses to write one. It takes no argument on purpose: there is nothing
 * it could be handed that would change its answer.
 */
export function initialQuorumInputs(): QuorumInputs {
  return { clergy: '', laity: '', bishop: '' };
}

/** Which field a parse refusal is about, so the form can point at it. */
export type QuorumField = 'clergy' | 'laity' | 'bishop';

export type QuorumParse =
  { ok: true; declaration: QuorumDeclaration } | { ok: false; field: QuorumField };

/** One field to a non-negative integer, or `null` if it is blank or not a plain
 *  count. `^\d+$` admits digits only — no sign, no decimal, no thousands
 *  separator, no whitespace inside — so a negative or a `1.5` is refused here
 *  rather than coerced. */
function parseField(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * The three fields to the worker's `{ clergy, laity, bishop }`, or the first field
 * that is not a lawful count — mirroring `validateQuorumClaim`
 * (`@cca/decides-worker/src/routes/setup.ts`): Clergy and Laity are non-negative
 * integers, the Bishop is 0 or 1 (a diocese has one Bishop or none). This is the
 * legible answer in front of the trigger, so the common operator slip — a blank
 * box, a stray letter, a 2 in the Bishop field — is caught before the request
 * rather than coming back as a 422.
 */
export function parseQuorum(inputs: QuorumInputs): QuorumParse {
  const clergy = parseField(inputs.clergy);
  if (clergy === null) return { ok: false, field: 'clergy' };
  const laity = parseField(inputs.laity);
  if (laity === null) return { ok: false, field: 'laity' };
  const bishop = parseField(inputs.bishop);
  // 0 or 1 — the trigger's `IN (0, 1)` arm. `> 1` is the operator error this
  // catches; `parseField` has already refused a negative and a non-integer.
  if (bishop === null || bishop > 1) return { ok: false, field: 'bishop' };
  return { ok: true, declaration: { clergy, laity, bishop } };
}

/** Whether all three fields parse — the gate on the open form's confirm button, so
 *  the empty and malformed cases never reach the server's 422. */
export function isQuorumComplete(inputs: QuorumInputs): boolean {
  return parseQuorum(inputs).ok;
}

// ═══════════════════════════════════════════════════════════════════════════
//  ABANDON — the one free-text reason.
// ═══════════════════════════════════════════════════════════════════════════

/** The upper bound the schema's `ballot_abandonment_requires_attribution` sets. */
export const ABANDON_REASON_MAX = 500;

/**
 * Whether an abandon reason is one the server will accept — non-blank and at most
 * 500 CODE POINTS. Counted with the spread, not `.length`, to match SQLite's
 * `length()` and the route's own `[...reason].length`, so a lawful reason carrying
 * an astral character is not refused one character early (the over-strict
 * direction cl 9(1)-(2) forbids). Gates the abandon confirm's button.
 */
export function isAbandonReasonValid(reason: string): boolean {
  if (reason.trim() === '') return false;
  return [...reason].length <= ABANDON_REASON_MAX;
}
